import React, { useState, useCallback } from 'react';

import {
  Tooltip as MUITooltip,
  TooltipProps as MUITooltipProps,
  ClickAwayListener,
} from '@mui/material';

import {
  useIsDesktop,
  useIsInsideContainer,
  useActionOnScroll,
} from '../hooks';

import useStyles from './Tooltip.styles';

/**
 * Интерфейс, определяющий свойства для компонента Tooltip.
 */
export interface TooltipProps {
  /**
   * Дочерний элемент, к которому привязан тултип.
   */
  children: React.ReactElement;

  /**
   * Текст заголовка или React-элемент, отображаемый в тултипе.
   */
  title: string | React.ReactElement;

  /**
   * Необязательное положение тултипа.
   * @default 'right'
   */
  placement?: MUITooltipProps['placement'];

  /**
   * Логическое значение, указывающее, отключен ли тултип.
   * @default false
   */
  disabled?: boolean;

  /**
   * Логическое значение, указывающее, является ли тултип интерактивным.
   * @default false
   */
  interactive?: boolean;

  /**
   * Необязательная функция обратного вызова, вызываемая при открытии тултипа.
   */
  onOpen?: () => void;

  /**
   * Необязательная функция обратного вызова, вызываемая при закрытии тултипа.
   */
  onClose?: () => void;

  /**
   * Логическое значение, указывающее, следует ли использовать инвертированные стили для тултипа.
   * @default false
   */
  inverted?: boolean;

  /**
   * Максимальная ширина.
   * Необязательный параметр для юнит-тестов.
   */
  maxWidth?: number | string;

  /**
   * Необязательная задержка (в миллисекундах) перед появлением тултипа.
   */
  enterDelay?: number;

  /**
   * Необязательный параметр для юнит-тестов.
   */
  testId?: string;
}

/**
 * Компонент Tooltip, который отображает тултип для указанного дочернего элемента.
 * Позволяет настраивать заголовок, положение и поведение тултипа.
 *
 * @param {TooltipProps} props - Свойства для компонента Tooltip.
 * @returns {React.ReactElement} - React-элемент, представляющий тултип.
 */

export const Tooltip = ({
  children,
  title,
  placement = 'right',
  disabled = false,
  interactive = false,
  maxWidth,
  onOpen,
  onClose,
  inverted = false,
  enterDelay,
  testId = 'tooltip-test-id',
}: TooltipProps) => {
  const { classes } = useStyles({ inverted, maxWidth });
  const isDesktop = useIsDesktop();
  const isInsideContainer = useIsInsideContainer();

  const [open, setOpen] = useState(false);

  const handleToggle = useCallback(() => {
    setOpen((prevState) => {
      if (prevState) {
        onClose?.();
      } else {
        onOpen?.();
      }
      return !prevState;
    });
  }, [onOpen, onClose]);

  const handleClickAway = useCallback(() => {
    if (open) {
      setOpen(false);
      onClose?.();
    }
  }, [open, onClose]);

  useActionOnScroll({
    active: !isDesktop && !disabled && open,
    onAction: handleClickAway,
    scrollSelector: isInsideContainer ? null : undefined,
  });

  if (disabled) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  const tooltipProps = {
    classes,
    arrow: true,
    title,
    placement,
    disableInteractive: !interactive,
    enterDelay,
    className: 'aqa_tooltip',
    'data-testid': testId,
  };

  if (!isDesktop) {
    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        <MUITooltip
          {...tooltipProps}
          open={open}
          disableFocusListener
          disableHoverListener
          disableTouchListener
        >
          {React.cloneElement(children, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              handleToggle();
            },
          })}
        </MUITooltip>
      </ClickAwayListener>
    );
  }

  return (
    <MUITooltip
      {...tooltipProps}
      enterTouchDelay={0}
      onOpen={onOpen}
      onClose={onClose}
    >
      {children}
    </MUITooltip>
  );
};
